import { useState } from "react";
import { MessageSquare, Headphones, AlertTriangle, Check, Hotel } from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";
import { useToast } from "../../../context/ToastContext";
import styles from "./CustomerRequestsPanel.module.css";

/**
 * What the customer wrote at checkout, in the two places it can be written.
 *
 * They are not the same thing and are deliberately not shown as one list:
 *
 *   Accommodation request — belongs to the STAY. It is forwarded to the supplier as the
 *     booking remark, so by the time it appears here it has already left the building. It is
 *     a request and never a confirmed service: the hotel answers it on the day.
 *
 *   Message to SUNSKY — addressed to us. A question, or a request for assistance during the
 *     trip. It is never forwarded to a hotel, because a hotel can neither arrange wheelchair
 *     assistance at an airport nor confirm it, and a request sent to the wrong party reads as
 *     handled when nobody has it.
 *
 * An assistance request arrives flagged ACTION REQUIRED, and that flag is not editable here.
 * It states what the request IS, not how far along it is — a flag the person who has not done
 * the work yet can clear is a flag that stops meaning anything. What staff change is the
 * STATUS (where we are with it) and, separately, whether the provider has CONFIRMED it: those
 * are different claims, and only the second one can be repeated to a customer.
 */

const STATUSES = [
  { id: "Open", label: "Open" },
  { id: "InProgress", label: "In progress" },
  { id: "Resolved", label: "Resolved" },
];

const TYPE_LABEL = {
  General: "General question or remark",
  Assistance: "Help or assistance during the trip",
};

const CustomerRequestsPanel = ({ bookingId, products, messages, isLocked, onChanged }) => {
  const { showToast } = useToast();
  const [busyId, setBusyId] = useState(null);

  // The stay request lives on the hotel product, which is where the supplier call reads it.
  const hotelRequests = (products || [])
    .filter((p) => p.productType === "Hotel" && p.productDetails?.specialRequests)
    .map((p) => ({
      id: p.id,
      hotelName: p.productDetails?.hotelName || "Accommodation",
      text: p.productDetails.specialRequests,
      sent: Boolean(p.supplierReference),
    }));

  const list = messages || [];
  if (!hotelRequests.length && !list.length) return null;

  const patch = async (msg, body) => {
    setBusyId(msg.id);
    try {
      const { data } = await axiosInstance.patch(`/bookings/${bookingId}/messages/${msg.id}`, body);
      if (data?.success) {
        showToast("Message updated", "success");
        onChanged?.();
      } else {
        showToast(data?.message || "Update failed", "error");
      }
    } catch (e) {
      showToast(e?.response?.data?.message || e.message || "Update failed", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.wrap}>
      {hotelRequests.length > 0 && (
        <section className={styles.card}>
          <header className={styles.head}>
            <span className={`${styles.ico} ${styles.icoHotel}`}><Hotel size={16} /></span>
            <div>
              <h3 className={styles.title}>Special requests for the stay</h3>
              <p className={styles.sub}>
                Sent to the accommodation with the reservation. Subject to availability — never a
                confirmed service or a guaranteed upgrade.
              </p>
            </div>
          </header>
          {hotelRequests.map((r) => (
            <div className={styles.item} key={r.id}>
              <div className={styles.itemHead}>
                <span className={styles.who}>{r.hotelName}</span>
                <span className={`${styles.tag} ${r.sent ? styles.tagSent : styles.tagPending}`}>
                  {r.sent ? "Forwarded to supplier" : "Forwarded on confirmation"}
                </span>
              </div>
              <p className={styles.quote}>{r.text}</p>
            </div>
          ))}
        </section>
      )}

      {list.length > 0 && (
        <section className={styles.card}>
          <header className={styles.head}>
            <span className={`${styles.ico} ${styles.icoSunsky}`}><Headphones size={16} /></span>
            <div>
              <h3 className={styles.title}>Messages to SUNSKY</h3>
              <p className={styles.sub}>
                Addressed to us, not to the hotel. Assistance requests must be taken up with the
                airline or the service provider before they can be confirmed to the customer.
              </p>
            </div>
          </header>

          {list.map((m) => {
            const busy = busyId === m.id;
            const confirmed = Boolean(m.confirmedAt);
            return (
              <div className={styles.item} key={m.id}>
                <div className={styles.itemHead}>
                  <span className={styles.who}>
                    <MessageSquare size={14} /> {TYPE_LABEL[m.messageType] || m.messageType}
                  </span>
                  {m.actionRequired && (
                    <span className={`${styles.tag} ${styles.tagAction}`}>
                      <AlertTriangle size={12} /> Action required
                    </span>
                  )}
                  {confirmed && (
                    <span className={`${styles.tag} ${styles.tagSent}`}>
                      <Check size={12} /> Confirmed by provider
                    </span>
                  )}
                </div>

                {Array.isArray(m.travelers) && m.travelers.length > 0 && (
                  <p className={styles.meta}>
                    For: {m.travelers.map((t) => t.name || `Traveller ${t.index}`).join(", ")}
                  </p>
                )}

                <p className={styles.quote}>{m.message}</p>

                <div className={styles.actions}>
                  <div className={styles.statuses}>
                    {STATUSES.map((st) => (
                      <button
                        type="button"
                        key={st.id}
                        disabled={isLocked || busy}
                        className={`${styles.status} ${m.status === st.id ? styles.statusOn : ""}`}
                        onClick={() => m.status !== st.id && patch(m, { status: st.id })}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                  {/* Separate from the status on purpose: "we have dealt with it" and "the
                      provider has confirmed it" are different claims about the same request. */}
                  <button
                    type="button"
                    disabled={isLocked || busy}
                    className={`${styles.confirm} ${confirmed ? styles.confirmOn : ""}`}
                    onClick={() => patch(m, { confirmed: !confirmed })}
                  >
                    <Check size={14} /> {confirmed ? "Confirmed" : "Mark confirmed by provider"}
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
};

export default CustomerRequestsPanel;
